import { HTMLAttributeAnchorTarget, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { PointOfInterestCard as PointOfInterestCardDesignSystem } from '../../../design-system/components/card/pointOfInterest/PointOfInterestCard';
import { ReviewRatingProps } from '../../../design-system/components/review/rating/ReviewRating';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { PointOfInterest } from '../../../resources/pointOfInterest/domain/types/PointOfInterest';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { IntrinsicElement } from '../../utilities/types/IntrinsicElement';
import { PickDomain } from '../../utilities/types/PickDomain';
import { ReviewRatingFallback } from '../ReviewRating/ReviewRatingFallback';

type Props = {
    pointOfInterest: PickDomain<
        PointOfInterest,
        'id' | 'slug' | 'name',
        {
            description: PointOfInterest['description'] | null;
            averageRating: Pick<PointOfInterest['averageRating'], 'rating'> | null;
            picture: PointOfInterest['picture'] | null;
            destination: PickDomain<PointOfInterest['destination'], 'slug'>;
        }
    >;
    withDescription?: boolean;
    placeNameElement?: IntrinsicElement;
    obfuscate?: boolean;
    target?: HTMLAttributeAnchorTarget;
    trackingInformation: {
        click: TrackingProperties<typeof ampli.poiClicked>;
    };
};

export function PointOfInterestCard({
    pointOfInterest,
    obfuscate,
    placeNameElement,
    withDescription,
    target,
    trackingInformation,
}: Props) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const reviewRatingProps: ReviewRatingProps | null = pointOfInterest.averageRating
        ? {
              ratingValue: pointOfInterest.averageRating.rating.value,
              maxRating: pointOfInterest.averageRating.rating.max,
              minRating: pointOfInterest.averageRating.rating.min,
              ariaLabel: (rating) => {
                  return formatMessage(
                      {
                          id: 'global.component.reviewRating.iconsDescription',
                      },
                      {
                          rating: rating.value,
                          maxRating: rating.max,
                      },
                  );
              },
              fallback: (error) => <ReviewRatingFallback error={error} />,
          }
        : null;

    const handleClick = useCallback(() => {
        if (trackingInformation?.click) {
            trackEvent((ampli, defaultProperties) => {
                ampli.poiClicked({
                    ...defaultProperties,
                    ...trackingInformation.click,
                });
            });
        }
    }, [trackEvent, trackingInformation?.click]);

    return (
        <PointOfInterestCardDesignSystem
            key={pointOfInterest.id}
            path={generatePath('place', {
                destinationSlug: pointOfInterest.destination.slug,
                placeId: pointOfInterest.id,
                placeSlug: pointOfInterest.slug,
            })}
            image={
                pointOfInterest.picture
                    ? {
                          src: pointOfInterest.picture.id.toString(),
                          alt: pointOfInterest.picture.legend,
                          loader: imageLoaderForId({
                              aspectRatio: PointOfInterestCardDesignSystem.PICTURE_RATIO,
                          }),
                          sizes: '(min-width: 1400px) 400px, (min-width: 1200px) 350px, 300px',
                      }
                    : undefined
            }
            placeName={pointOfInterest.name}
            placeNameElement={placeNameElement}
            reviewRatingProps={reviewRatingProps}
            target={target}
            obfuscate={obfuscate}
            description={pointOfInterest.description ?? ''}
            withDescription={withDescription}
            onClick={handleClick}
        />
    );
}
